/**
 * features/hr/DeductionEntry.tsx
 * ------------------------------------------------------------------
 * Cross-staff entry for ONE deduction type (optionally scoped to a
 * single bank, for bank-scheme tabs). Three ways in:
 *   1. Pick a staff member and fill the proven type-driven form
 *      (DeductionFormModal, reused verbatim).
 *   2. Download a CSV template pre-filled with a staff cohort, fill in
 *      the amounts offline, and upload it to create many at once.
 *   3. Edit any existing record from the list.
 *
 * Whatever the route, each record is an HrDeduction, so the payroll
 * engine amortises it onto pay runs, shows it on the staff profile,
 * and recovers it at exit — unchanged.
 *
 * THE LIST (Oct 2026): one row per member of staff with their running
 * records totalled — a consolidated teacher appears once — and the loans
 * folded into a consolidation, paid off or archived on their own
 * "Old & consolidated" list (lib/loanList).
 * ------------------------------------------------------------------
 */
import { Fragment, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Plus, ExternalLink, Pencil, Download, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { arrangeLoans, oldLoanReason } from '@/lib/loanList';
import { useHrData } from '@/features/hr/useHrData';
import StaffPicker from '@/features/hr/StaffPicker';
import {
  DeductionFormModal,
  seedForm,
  recompute,
  RECOMPUTE_TRIGGERS,
  type FormState,
} from '@/features/hr/DeductionsManager';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import {
  DEDUCTION_TYPES,
  deductionMonthlyAmount,
  deductionCurrentBalance,
  payslipDeductionLabel,
  fmtPula,
  round2,
} from '@/lib/hrPayroll';
import type {
  DeductionType,
  HrDeduction,
  DeductionStatus,
  FieldDef,
  FieldValue,
} from '@/lib/hrPayroll';
import {
  staffFullName,
  STAFF_CATEGORY_LABELS,
  type HrStaff,
  type StaffCategory,
} from '@/lib/hr';
import { parseCsv, downloadCsv, makeColFinder, cell } from '@/lib/hrImport';
import { SoftCard, AccentButton } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';

const STATUS_TONE: Record<DeductionStatus, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  paid_off: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  archived: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  suspended: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

/** Minimal RFC-4180-ish CSV cell quoting. */
function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

/** FormState (strings) -> stored values, matching the modal's buildValues. */
function buildValues(fields: FieldDef[], form: FormState): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const f of fields) {
    const raw = form[f.key];
    if (f.kind === 'checkbox') {
      out[f.key] = Boolean(raw);
    } else if (f.kind === 'number' || f.kind === 'money') {
      out[f.key] = raw === '' || raw === undefined ? null : round2(Number(raw) || 0);
    } else {
      const s = String(raw ?? '').trim();
      out[f.key] = s === '' ? null : s;
    }
  }
  return out;
}

export default function DeductionEntry({
  type,
  bank,
  onOpenStaff,
}: {
  type: DeductionType;
  bank?: string;
  onOpenStaff?: (id: string) => void;
}) {
  const data = useHrData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const cfg = DEDUCTION_TYPES[type];
  const [pickStaffId, setPickStaffId] = useState('');
  const [editor, setEditor] = useState<{ staff: HrStaff; existing?: HrDeduction } | null>(
    null,
  );
  const [cohort, setCohort] = useState<'all' | StaffCategory>('all');
  const [busy, setBusy] = useState(false);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const fileRef = useRef<HTMLInputElement>(null);

  const showBalance = !!cfg.balanceKey;
  const presetForBank = bank ? { bankName: bank } : undefined;

  // Editable columns for the CSV template: skip computed, checkboxes, and
  // the bank column when the tab already fixes the bank.
  const fieldCols = useMemo(
    () =>
      cfg.fields.filter(
        (f) => !f.computed && f.kind !== 'checkbox' && !(bank && f.key === 'bankName'),
      ),
    [cfg, bank],
  );

  const rows = useMemo(
    () =>
      data.deductions
        .filter((d) => d.type === type && (!bank || String(d.values?.bankName ?? '') === bank))
        .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    [data.deductions, type, bank],
  );

  const arranged = useMemo(() => arrangeLoans(rows), [rows]);
  const [view, setView] = useState<'current' | 'old'>('current');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const cohortStaff = useMemo(
    () =>
      data.staff.filter(
        (s) => s.status === 'active' && (cohort === 'all' || s.category === cohort),
      ),
    [data.staff, cohort],
  );

  if (data.loading) return <Spinner label="Loading…" />;

  function openAdd() {
    const s = data.staffById(pickStaffId);
    if (s) setEditor({ staff: s });
  }
  function openEdit(d: HrDeduction) {
    const s = data.staffById(d.staffId);
    if (s) setEditor({ staff: s, existing: d });
  }
  function closeEditor() {
    setEditor(null);
    setPickStaffId('');
  }

  function downloadTemplate() {
    const header = ['Staff ID', 'Staff Number', 'Staff Name', ...fieldCols.map((f) => f.label)];
    const body = cohortStaff.map((s) => [
      s.id,
      s.employeeNo ?? '',
      staffFullName(s),
      ...fieldCols.map(() => ''),
    ]);
    const tag = bank ? `${type}-${bank.replace(/\s+/g, '_')}` : type;
    downloadCsv(`${tag}-template-${cohort}.csv`, toCsv([header, ...body]));
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => void importCsv(String(reader.result ?? ''));
    reader.readAsText(file);
  }

  async function importCsv(text: string) {
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Importing…' });
    try {
      const { headers, rows: dataRows } = parseCsv(text);
      const find = makeColFinder(headers);
      const idCol = find(['Staff ID', 'staffid', 'id']);
      const employerPct = data.payrollSettings?.medicalAidEmployerPct ?? 50;
      const reqKey =
        cfg.fields.find((f) => f.required && (f.kind === 'money' || f.kind === 'number'))
          ?.key ?? cfg.amountKey;
      let created = 0;
      let skipped = 0;
      for (const row of dataRows) {
        const sid = cell(row, idCol).trim();
        const staff = data.staffById(sid);
        if (!staff) {
          skipped += 1;
          continue;
        }
        const form: FormState = seedForm(type);
        for (const f of fieldCols) {
          const ci = find([f.label]);
          if (ci >= 0) {
            const v = cell(row, ci).trim();
            if (v) form[f.key] = v;
          }
        }
        if (bank) form.bankName = bank;
        for (const k of RECOMPUTE_TRIGGERS[type] ?? []) {
          Object.assign(form, recompute(type, form, employerPct, k));
        }
        if (
          reqKey &&
          (form[reqKey] === undefined ||
            String(form[reqKey]).trim() === '' ||
            Number(form[reqKey]) === 0)
        ) {
          skipped += 1;
          continue;
        }
        await hr.deductions.add({
          staffId: sid,
          type,
          label: cfg.label,
          status: 'active' as DeductionStatus,
          values: buildValues(cfg.fields, form),
        });
        created += 1;
      }
      await recordHrAudit({
        action: 'deduction.import',
        actor,
        after: { type, created, skipped, source: 'csv' },
      });
      setSaveModal({
        show: true,
        status: 'success',
        message: `Imported ${created} record${created === 1 ? '' : 's'}${
          skipped ? `; skipped ${skipped} (blank or unmatched staff)` : ''
        }.`,
      });
    } catch (err) {
      setSaveModal({
        show: true,
        status: 'error',
        message: (err as Error).message || 'Could not import the file.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* ---- Add one ---- */}
      <SoftCard className="p-4">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Add {cfg.label.toLowerCase()}
          {bank ? ` · ${bank}` : ''} for a staff member
        </p>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 min-w-0">
            <StaffPicker
              staff={data.staff}
              value={pickStaffId}
              onChange={setPickStaffId}
              activeOnly
            />
          </div>
          <AccentButton icon={Plus} disabled={!pickStaffId} onClick={openAdd}>
            Add {cfg.label}
          </AccentButton>
        </div>
      </SoftCard>

      {/* ---- Bulk via CSV ---- */}
      <SoftCard className="p-4">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          Bulk add by CSV
        </p>
        <p className="text-xs text-gray-500 mb-3">
          Download a template pre-filled with a staff cohort, enter the amounts, then upload it.
          Blank rows and unmatched staff are skipped.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Cohort
            <select
              value={cohort}
              onChange={(e) => setCohort(e.target.value as 'all' | StaffCategory)}
              className="ml-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
            >
              <option value="all">All active staff</option>
              {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
                <option key={c} value={c}>
                  {STAFF_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            <AccentButton icon={Download} variant="soft" onClick={downloadTemplate}>
              Download template ({cohortStaff.length})
            </AccentButton>
            <AccentButton
              icon={Upload}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? 'Importing…' : 'Upload CSV'}
            </AccentButton>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFile}
            />
          </div>
        </div>
      </SoftCard>

      {/* ---- List: one row per member of staff; old and consolidated loans apart (Oct 2026) ---- */}
      <SoftCard className="p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {([['current', `Current (${arranged.current.length})`], ['old', `Old & consolidated (${arranged.old.length})`]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              aria-pressed={view === k}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${view === k
                ? 'bg-[var(--accent-strong)] text-white border-transparent'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === 'current' && (arranged.current.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing running.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="py-2 pr-4 font-medium">Staff</th>
                  <th className="py-2 px-4 font-medium text-right">Monthly</th>
                  {showBalance && <th className="py-2 px-4 font-medium text-right">Remaining</th>}
                  <th className="py-2 px-4 font-medium">Status</th>
                  <th className="py-2 pl-4 font-medium text-right">Edit</th>
                </tr>
              </thead>
              <tbody>
                {arranged.current.map((row) => {
                  const s = data.staffById(row.staffId);
                  const many = row.records.length > 1;
                  const open = expanded.has(row.staffId);
                  return (
                    <Fragment key={row.staffId}>
                      <tr className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2.5 pr-4">
                          <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => onOpenStaff?.(row.staffId)}>
                            {s ? staffFullName(s) : row.staffId}
                            <ExternalLink size={13} />
                          </button>
                        </td>
                        <td className="py-2.5 px-4 text-right">{fmtPula(row.monthly)}</td>
                        {showBalance && <td className="py-2.5 px-4 text-right">{row.remaining == null ? '—' : fmtPula(row.remaining)}</td>}
                        <td className="py-2.5 px-4">
                          {many ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{row.records.length} {type === 'salary_advance' ? 'advances' : 'loans'}</span>
                          ) : (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[row.records[0].status]}`}>{row.records[0].status.replace('_', ' ')}</span>
                          )}
                        </td>
                        <td className="py-2.5 pl-4 text-right">
                          {many ? (
                            <button className="inline-flex items-center gap-1 text-gray-500 hover:text-primary hover:underline" onClick={() => toggle(row.staffId)}>
                              {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? 'Hide' : 'Show each'}
                            </button>
                          ) : (
                            <button className="inline-flex items-center gap-1 text-gray-500 hover:text-primary hover:underline" onClick={() => openEdit(row.records[0])}>
                              <Pencil size={13} /> Edit
                            </button>
                          )}
                        </td>
                      </tr>
                      {many && open && row.records.map((d) => {
                        const remaining = deductionCurrentBalance(d);
                        return (
                          <tr key={d.id} className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 text-xs">
                            <td className="py-2 pr-4 pl-6 text-gray-500">{payslipDeductionLabel(d)}{d.values?.loanAmount ? ` · ${fmtPula(Number(d.values.loanAmount))}` : ''}</td>
                            <td className="py-2 px-4 text-right">{fmtPula(deductionMonthlyAmount(d))}</td>
                            {showBalance && <td className="py-2 px-4 text-right">{remaining == null ? '—' : fmtPula(remaining)}</td>}
                            <td className="py-2 px-4"><span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_TONE[d.status]}`}>{d.status.replace('_', ' ')}</span></td>
                            <td className="py-2 pl-4 text-right">
                              <button className="inline-flex items-center gap-1 text-gray-500 hover:text-primary hover:underline" onClick={() => openEdit(d)}>
                                <Pencil size={13} /> Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {view === 'old' && (arranged.old.length === 0 ? (
          <p className="text-sm text-gray-500">No old or consolidated records.</p>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-xs text-gray-500 mb-2">Not deducted any more — kept for the record. A loan folded into a consolidated one is recovered through the consolidated loan on the Current list.</p>
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="py-2 pr-4 font-medium">Staff</th>
                  <th className="py-2 px-4 font-medium text-right">Amount</th>
                  <th className="py-2 px-4 font-medium">Why it is here</th>
                  <th className="py-2 pl-4 font-medium text-right">View</th>
                </tr>
              </thead>
              <tbody>
                {arranged.old.map((d) => {
                  const s = data.staffById(d.staffId);
                  return (
                    <tr key={d.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2.5 pr-4">
                        <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => onOpenStaff?.(d.staffId)}>
                          {s ? staffFullName(s) : d.staffId}
                          <ExternalLink size={13} />
                        </button>
                      </td>
                      <td className="py-2.5 px-4 text-right">{d.values?.loanAmount ? fmtPula(Number(d.values.loanAmount)) : '—'}</td>
                      <td className="py-2.5 px-4 text-gray-600 dark:text-gray-300">{oldLoanReason(d)}</td>
                      <td className="py-2.5 pl-4 text-right">
                        <button className="inline-flex items-center gap-1 text-gray-500 hover:text-primary hover:underline" onClick={() => openEdit(d)}>
                          <Pencil size={13} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </SoftCard>

      {editor && (
        <DeductionFormModal
          staff={editor.staff}
          type={type}
          existing={editor.existing}
          settings={data.payrollSettings}
          preset={presetForBank}
          onClose={closeEditor}
        />
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </div>
  );
}
